<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

defineOptions({
  name: "VCloseButton",
});

/**
 * The close button of every dialog, sheet, picker and drawer, after the App Store's: a 44px button with 10px corners
 * and a 22px line X in the text colour. On hover the X turns a quarter, on press it also grows to 120%, both over
 * 250ms ease; with reduced motion it stays still. Only the X moves, so the hit area never shifts under the pointer.
 * The focus ring is the 2px primary ring of the other controls. Placing it is up to the caller; inside a dialog the
 * host draws it through DialogClose.
 */
const { label = "" } = defineProps<{
  /** Accessible name; the translated "Sluiten" unless the button closes something more specific. */
  label?: string
}>();

const emit = defineEmits<{
  click: [event: MouseEvent]
}>();

const { t } = useI18n();
const ariaLabel = computed(() => label || t("base.close"));
</script>

<template>
  <button
    type="button"
    class="close-button flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-[10px] text-(--text) focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
    :aria-label="ariaLabel"
    data-close-button
    @click="emit('click', $event)"
  >
    <!-- The boards' line X on a 24px grid with a 2px round stroke. -->
    <svg
      class="close-button-glyph"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  </button>
</template>

<style scoped lang="scss">
.close-button {
  .close-button-glyph {
    transition: transform 250ms ease;
  }

  &:hover .close-button-glyph {
    transform: rotate(90deg);
  }

  &:active .close-button-glyph {
    transform: rotate(90deg) scale(1.2);
  }

  @media (prefers-reduced-motion: reduce) {
    .close-button-glyph,
    &:hover .close-button-glyph,
    &:active .close-button-glyph {
      transition: none;
      transform: none;
    }
  }
}
</style>
